import React, { useCallback, useEffect, useMemo, useState } from "react";

import { addMonths, formatDateParam, parseDateParam } from "helpers";
import { formatUnitPrice } from "services/api/pricing/consts";
import { previewPricingOverrideAPI } from "services/api/pricing/overrideApi";
import { PricingOverridePreview } from "services/api/pricing/overrideTypes";
import { PricingAdjustmentType } from "services/api/pricing/schemeTypes";
import { SubscriptionOverrideFormValues } from "services/api/subscriptions/types";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { Badge } from "ui/components/Badge";
import { Button } from "ui/components/Button/Button";
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextField } from "ui/components/TextField/TextField";

import { SubscriptionOverrideModalProps } from "../../types";
import { SUBSCRIPTION_OVERRIDE_FORM_SCHEMA } from "./validation";

const ADJUSTMENT_OPTIONS: DropdownOption[] = [
  { value: "Percent", text: "Postotak — prati cjenik" },
  { value: "Absolute", text: "Fiksno — ne prati cjenik" },
];

/** Zadani rok je 12 mjeseci — poseban uvjet bez roka se ne sprema. */
const DEFAULT_MONTHS = 12;

/**
 * Unos jednog posebnog uvjeta u čarobnjaku.
 *
 * Ne sprema ništa — vraća vrijednosti u stanje čarobnjaka, koje ide na potpis zajedno s
 * pretplatom. Pretpregled se traži od backenda dok predstavnik tipka: ni cijena ni prag
 * odobrenja se ne računaju u pregledniku.
 *
 * Naš trošak i marža se namjerno ne prikazuju — u čarobnjaku nemaju što tražiti; verdikt
 * `outcome` nosi sve što predstavnik treba znati.
 */
const SubscriptionOverrideModal: React.FC<SubscriptionOverrideModalProps> = (props) => {
  const {
    show,
    tenantId,
    startsOn,
    pricingSchemeId,
    overridableKeys,
    existing,
    onSave,
    onClose,
  } = props;

  const [formValues, setFormValues] = useState<SubscriptionOverrideFormValues>({
    keyCode: "",
    adjustmentType: "Percent",
    value: 0,
    reason: "",
    validFrom: startsOn,
    validTo: formatDateParam(addMonths(parseDateParam(startsOn)!, DEFAULT_MONTHS)) ?? startsOn,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [preview, setPreview] = useState<PricingOverridePreview | null>(null);

  useEffect(() => {
    if (!show) return;

    setErrors({});
    setPreview(null);
    setFormValues(
      existing ?? {
        keyCode: "",
        adjustmentType: "Percent",
        value: 0,
        reason: "",
        validFrom: startsOn,
        validTo:
          formatDateParam(addMonths(parseDateParam(startsOn)!, DEFAULT_MONTHS)) ?? startsOn,
      }
    );
  }, [show, existing, startsOn]);

  /*
    Razlaganje se osvježava dok predstavnik tipka. Shema se šalje kao prijedlog jer u čarobnjaku
    još nije dodijeljena klijentu.
  */
  useEffect(() => {
    // Vrijednost se hvata prije odgode: unutar `setTimeout` se sužavanje tipa gubi.
    const value = formValues.value;

    // Bez upisane vrijednosti nema se što razložiti; poslužitelj bi ionako vratio grešku.
    if (!tenantId || !formValues.keyCode || value === null) {
      setPreview(null);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const result = await previewPricingOverrideAPI(
          tenantId,
          formValues.keyCode,
          formValues.adjustmentType,
          value,
          startsOn,
          pricingSchemeId
        );

        if (!cancelled) setPreview(result);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    tenantId,
    startsOn,
    pricingSchemeId,
    formValues.keyCode,
    formValues.adjustmentType,
    formValues.value,
  ]);

  const keyOptions: DropdownOption[] = useMemo(
    () => overridableKeys.map((key) => ({ value: key.keyCode, text: key.name })),
    [overridableKeys]
  );

  const handleKeyChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
    setFormValues((previous) => ({ ...previous, keyCode: item.value }));
  }, []);

  const handleTypeChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
    setFormValues((previous) => ({
      ...previous,
      adjustmentType: item.value as PricingAdjustmentType,
    }));
  }, []);

  const handleValueChange = useCallback((value: number | null) => {
    setFormValues((previous) => ({ ...previous, value }));
  }, []);

  const handleReasonChange = useCallback((event: any) => {
    const value = event?.target?.value ?? "";

    setFormValues((previous) => ({ ...previous, reason: value }));
  }, []);

  const handleValidFromChange = useCallback((_name: string, date: Date | null) => {
    const formatted = formatDateParam(date);

    if (!formatted) return;

    setFormValues((previous) => ({ ...previous, validFrom: formatted }));
  }, []);

  const handleValidToChange = useCallback((_name: string, date: Date | null) => {
    const formatted = formatDateParam(date);

    if (!formatted) return;

    setFormValues((previous) => ({ ...previous, validTo: formatted }));
  }, []);

  const handleSave = useCallback(() => {
    if (!validateYup(formValues as any, SUBSCRIPTION_OVERRIDE_FORM_SCHEMA, setErrors)) {
      return;
    }

    onSave(formValues);
  }, [formValues, onSave]);

  const isForbidden = preview?.outcome === "Forbidden";

  return (
    <Modal show={show} onHide={onClose} size="medium" position="centered">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={existing ? "Izmjena posebnog uvjeta" : "Novi poseban uvjet"}
        onHide={onClose}
      />

      <ModalBody>
        <div className="flex flex-col gap-5 p-2">
          <div>
            <Dropdown
              name="keyCode"
              label="Ključ"
              value={formValues.keyCode}
              options={keyOptions}
              onChange={handleKeyChange}
              error={errors.keyCode}
              required
              width="full"
            />
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Dropdown
                name="adjustmentType"
                label="Tip"
                value={formValues.adjustmentType}
                options={ADJUSTMENT_OPTIONS}
                onChange={handleTypeChange}
                width="full"
              />
            </div>

            <div className="w-[180px]">
              <NumberField
                name="value"
                label={formValues.adjustmentType === "Absolute" ? "€" : "%"}
                value={formValues.value}
                onChange={handleValueChange}
                decimals={formValues.adjustmentType === "Absolute" ? 4 : 2}
                error={errors.value}
                size="full"
              />
            </div>
          </div>

          {/* ŽIVO RAZLAGANJE — sve brojke dolaze s backenda */}
          {preview && (
            <div className="border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-700 mb-3">Efektivna cijena</p>

              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Cjenik</p>
                  <p className="font-semibold text-slate-700">
                    {formatUnitPrice.format(preview.standard)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Nakon grupe</p>
                  <p className="font-semibold text-slate-700">
                    {formatUnitPrice.format(preview.afterScheme)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Efektivna</p>
                  <p className="font-bold text-slate-800">
                    {formatUnitPrice.format(preview.effective)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Ukupan popust</p>
                  <p className="font-semibold text-slate-700">
                    {preview.discountPercent.toFixed(1).replace(".", ",")} %
                  </p>
                </div>
              </div>

              <div className="mt-3">
                {preview.outcome === "Forbidden" && (
                  <div className="border border-red-200 bg-red-50 p-3">
                    <Badge text="Nije dopušteno" variant="danger" height="small" showDot />
                    <p className="text-xs text-red-800 mt-2">
                      Efektivna cijena ne bi bila iznad naše nabave. To se ne odobrava ni uz
                      suglasnost voditelja.
                    </p>
                  </div>
                )}

                {preview.outcome === "NeedsApproval" && (
                  <div className="border border-amber-200 bg-amber-50 p-3">
                    <Badge text="Traži odobrenje" variant="warning" height="small" showDot />
                    <p className="text-xs text-amber-800 mt-2">
                      Pretplata će se potpisati, ali ostaje na čekanju dok je netko ne odobri.
                      Do tada se poseban uvjet ne primjenjuje.
                    </p>
                  </div>
                )}

                {preview.outcome === "Allowed" && (
                  <Badge text="Prolazi bez odobrenja" variant="success" height="small" showDot />
                )}
              </div>
            </div>
          )}

          <div>
            <TextField
              type="text"
              name="reason"
              label="Razlog"
              placeholder="Zašto se popust daje"
              value={formValues.reason}
              onChange={handleReasonChange}
              error={errors.reason}
              required
              width="full"
            />
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-[220px]">
              <DatePicker
                name="validFrom"
                label="Vrijedi od"
                value={parseDateParam(formValues.validFrom)}
                onChange={handleValidFromChange}
                error={errors.validFrom}
                required
                width="full"
              />
            </div>

            <div className="w-[220px]">
              <DatePicker
                name="validTo"
                label="Vrijedi do"
                value={parseDateParam(formValues.validTo)}
                onChange={handleValidToChange}
                error={errors.validTo}
                required
                width="full"
              />
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Rok je obavezan. Po isteku klijent pada natrag u grupu, uz zapis u povijesti.
          </p>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="outlined" children="Odustani" onClick={onClose} />
        <Button
          variant="primary"
          children="Spremi"
          onClick={handleSave}
          disabled={isForbidden}
        />
      </ModalFooter>
    </Modal>
  );
};

export default SubscriptionOverrideModal;
